import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileCheck2, Globe, Pencil, Trash2 } from 'lucide-react';
import {
  EDF_AUTHORITIES,
  EDF_AUTHORITY_LABELS,
  EDPMS_CLOSURE_ROUTE_LABELS,
  type EdfAuthority,
  type EdfMonth,
  type EdpmsClosureRoute,
  type ExportInvoiceRow,
  type ExportsRegister,
} from '@fint/validation';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useToday } from '@/hooks/useOrgSettings';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { PageHeader } from '@/app/DashboardLayout';
import { Attachments } from '@/features/common/Attachments';
import { cn } from '@/lib/cn';
import { financialYearOf, financialYearPeriod, formatDate, monthPeriod, type Period } from '@/lib/dates';
import {
  Badge, Button, Card, CardFacts, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, Money, PeriodPicker,
  SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, SkeletonTable, TBody, TD, TH, THead, TR, Table, Textarea,
} from '@/features/common/ui';

const STATUS_BADGE: Record<EdfMonth['status'], { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  filed: { label: 'Filed', variant: 'success' },
  due: { label: 'Due', variant: 'warning' },
  overdue: { label: 'Overdue', variant: 'danger' },
};

/** "Oct 2026" from "2026-10", through the same table the period picker labels with. */
function monthLabel(month: string): string {
  return monthPeriod(Number(month.slice(0, 4)), Number(month.slice(5, 7))).label;
}

/**
 * The Indian financial year a date falls in, April to March.
 *
 * April whatever the book's own `fiscalYearStartMonth`: FEMA's clock and the
 * LUT's year are the Indian financial year, and a book that closes in
 * December still files its EDFs against that.
 */
function indianFinancialYear(date: string): Period {
  return financialYearPeriod(financialYearOf(date, 4).startYear, 4);
}

/**
 * Exports: what an export invoice still owes after it is sent.
 *
 * ── WHY A PAGE OF ITS OWN ──
 * GSTR-1 Table 6A is the GST half of an export, and it is done when the
 * return is filed. The FEMA half is not: from 1 October 2026 every month's
 * export invoices go to the bank on one EDF within thirty days of the month
 * end, and each invoice's money has to come home within nine months, proved
 * by the bank's FIRA/FIRC and closed in EDPMS. Those are dates that run for
 * a year after the invoice, which is why they are a register and not a
 * return.
 *
 * ── TWO LISTS, IN THE ORDER THEY FALL DUE ──
 * The month's declaration first, because it is due first and it is due for
 * every export at once; then the invoices, each with its own realisation
 * clock. Nothing here is computed in the browser - every date and status is
 * the server's, so the review, the agent and this page agree.
 *
 * `?month=2026-10` (the review's "Open Exports") opens on the financial year
 * holding that month and brings the month into view.
 */
export function ExportsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const [searchParams] = useSearchParams();
  const focusMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(searchParams.get('month') ?? '') ? searchParams.get('month') : null;
  const today = useToday();
  const [period, setPeriod] = useState<Period>(() => indianFinancialYear(focusMonth ? `${focusMonth}-01` : today));
  const [editingMonth, setEditingMonth] = useState<EdfMonth | null>(null);
  const [closing, setClosing] = useState<ExportInvoiceRow | null>(null);
  const queryClient = useQueryClient();

  const register = useQuery({
    queryKey: ['exports', org?.id, period.from, period.to],
    queryFn: () => api.get<ExportsRegister>(`/exports${qs({ from: period.from, to: period.to })}`),
    enabled: !!org?.id,
  });

  const canManage = perms.can('settings', 'manage');
  const base = register.data?.baseCurrency ?? 'INR';

  // Scrolled to once, when the month first has somewhere to be. Every
  // refetch after a save would otherwise yank the page back to it.
  const scrolled = useRef(false);
  useEffect(() => {
    if (!focusMonth || scrolled.current || !register.data) return;
    const target = document.getElementById(`edf-${focusMonth}`);
    if (!target) return;
    scrolled.current = true;
    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focusMonth, register.data]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['exports', org?.id] });
    // A filed EDF answers export_edf_due; a closure or extension moves export_realisation_due.
    void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
  };

  const remove = useMutation({
    mutationFn: (month: string) => api.delete(`/exports/edf/${month}`),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader title="Exports" subtitle="The bank's declaration for each month, and each export invoice until its money is home." />

      <div className="space-y-4 px-4 pb-10 sm:px-5">
        <PeriodPicker value={period} onChange={setPeriod} />

        {register.isError ? (
          <Card>
            <ErrorState error={register.error} onRetry={() => register.refetch()} />
          </Card>
        ) : register.isLoading || !register.data ? (
          <Card>
            <SkeletonTable rows={6} cols={5} />
          </Card>
        ) : (
          <>
            {register.data.notes.length ? (
              <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground" data-testid="exports-notes">
                {register.data.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}

            <section className="space-y-3" aria-labelledby="edf-heading">
              <h2 id="edf-heading" className="text-sm font-semibold">
                Monthly EDF
              </h2>
              {remove.error ? (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {remove.error instanceof ApiError ? remove.error.message : 'Something went wrong'}
                </p>
              ) : null}
              {register.data.months.length ? (
                register.data.months.map((month) => (
                  <MonthCard
                    key={month.month}
                    month={month}
                    base={base}
                    highlighted={month.month === focusMonth}
                    canManage={canManage}
                    removing={remove.isPending && remove.variables === month.month}
                    onEdit={() => setEditingMonth(month)}
                    onRemove={() => {
                      const files = month.declaration?.attachmentCount
                        ? ` The ${month.declaration.attachmentCount === 1 ? 'file' : `${month.declaration.attachmentCount} files`} attached to it go too.`
                        : '';
                      if (window.confirm(`Remove the EDF recorded for ${monthLabel(month.month)}?${files}`)) remove.mutate(month.month);
                    }}
                  />
                ))
              ) : (
                <Card className="p-4 text-sm text-muted-foreground">
                  No export invoices dated from 1 October 2026 in this period, so no EDF is due for it.
                </Card>
              )}
            </section>

            <section className="space-y-3" aria-labelledby="export-invoices-heading">
              <h2 id="export-invoices-heading" className="text-sm font-semibold">
                Export invoices
              </h2>
              {register.data.invoices.length === 0 ? (
                <Card>
                  <EmptyState
                    icon={Globe}
                    title="No export invoices in this period"
                    description="An invoice is an export here when its customer's GST treatment is Overseas. Drafts and void invoices are left out."
                  />
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  {isPhone ? (
                    <InvoiceCards rows={register.data.invoices} base={base} canManage={canManage} onClose={setClosing} />
                  ) : (
                    <InvoiceTable rows={register.data.invoices} base={base} canManage={canManage} onClose={setClosing} />
                  )}
                </Card>
              )}
            </section>
          </>
        )}
      </div>

      {editingMonth ? (
        <EdfSheet
          month={editingMonth}
          today={today}
          onClose={() => setEditingMonth(null)}
          onSaved={() => {
            setEditingMonth(null);
            refresh();
          }}
        />
      ) : null}
      {closing ? (
        <ClosureSheet
          row={closing}
          onClose={() => setClosing(null)}
          onSaved={() => {
            setClosing(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

function MonthCard({
  month,
  base,
  highlighted,
  canManage,
  removing,
  onEdit,
  onRemove,
}: {
  month: EdfMonth;
  base: string;
  /** The month the address named, outlined so the eye lands on it after the scroll. */
  highlighted: boolean;
  canManage: boolean;
  removing: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const status = STATUS_BADGE[month.status];
  const declaration = month.declaration;
  return (
    <Card
      id={`edf-${month.month}`}
      className={cn('scroll-mt-4 space-y-3 p-4', highlighted && 'ring-2 ring-primary')}
      data-testid="edf-month"
      data-month={month.month}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{monthLabel(month.month)}</h3>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <p className="text-sm">
            {month.invoiceCount} {month.invoiceCount === 1 ? 'invoice' : 'invoices'} ·{' '}
            {month.totals.map((total, index) => (
              <span key={total.currency}>
                {index > 0 ? ' + ' : ''}
                <Money value={total.total} currency={total.currency} />
              </span>
            ))}
            {/* The rupee figure is what the bank's form asks for; skipped when it would repeat the line above. */}
            {month.totals.length !== 1 || month.totals[0]?.currency !== base ? (
              <span className="text-muted-foreground">
                {' '}
                (<Money value={month.baseTotal} currency={base} />)
              </span>
            ) : null}
          </p>
          <p className={cn('text-xs', month.status === 'overdue' ? 'text-destructive' : 'text-muted-foreground')}>
            Due at the bank by {formatDate(month.dueDate)}
            {declaration?.extendedTo ? `, extended to ${formatDate(declaration.extendedTo)}` : ''}
          </p>
        </div>
        {canManage ? (
          <div className="flex flex-wrap items-center gap-1">
            <Button size="sm" variant={declaration ? 'outline' : 'primary'} onClick={onEdit} data-testid="edf-record">
              {declaration ? <Pencil className="size-4" aria-hidden /> : <FileCheck2 className="size-4" aria-hidden />}
              {declaration ? 'Edit' : 'Record filing'}
            </Button>
            {declaration ? (
              <Button size="sm" variant="ghost" onClick={onRemove} disabled={removing} aria-label={`Remove the EDF for ${monthLabel(month.month)}`}>
                <Trash2 className="size-4" aria-hidden />
                Remove
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {declaration ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm" data-testid="edf-declaration">
          <dt className="text-muted-foreground">Filed</dt>
          <dd>
            {formatDate(declaration.filedOn)} with the {EDF_AUTHORITY_LABELS[declaration.authority]}
          </dd>
          {declaration.reference ? (
            <>
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="break-all font-mono text-xs leading-5">{declaration.reference}</dd>
            </>
          ) : null}
          {declaration.note ? (
            <>
              <dt className="text-muted-foreground">Note</dt>
              <dd className="break-words">{declaration.note}</dd>
            </>
          ) : null}
        </dl>
      ) : month.anyNonSoftware ? (
        <p className="text-xs text-muted-foreground">
          Some lines this month are not software: for those the EDF may instead be filed on or before the day
          payment arrives (Reg 3(2)(b)).
        </p>
      ) : null}

      {declaration ? (
        <Attachments
          ownerType="export_declaration"
          ownerId={declaration.id}
          title="Signed EDF and bank acknowledgement"
          emptyHint="Attach the signed form and the bank's acknowledgement - they are what proves the date."
        />
      ) : null}
    </Card>
  );
}

/** "Closed 12-07-2027 · Closed by the bank on realisation", or where it stands while open. */
function closureText(row: ExportInvoiceRow): string {
  if (row.closure.closedOn) {
    return `Closed ${formatDate(row.closure.closedOn)}${row.closure.route ? ` · ${EDPMS_CLOSURE_ROUTE_LABELS[row.closure.route]}` : ''}`;
  }
  return row.realisation.realised ? 'Paid; not yet closed' : 'Open';
}

function Realisation({ row }: { row: ExportInvoiceRow }) {
  return (
    <div className="space-y-0.5">
      {row.realisation.realised ? (
        <Badge variant="success">Realised</Badge>
      ) : (
        <p>
          <Money value={row.balanceDue} currency={row.currency} /> <span className="text-xs text-muted-foreground">to come</span>
        </p>
      )}
      {!row.realisation.realised ? (
        <p className="text-xs text-muted-foreground">
          By {formatDate(row.realisation.extendedTo ?? row.realisation.dueDate)}
          {row.realisation.extendedTo ? ` (bank extension; was ${formatDate(row.realisation.dueDate)})` : ''}
        </p>
      ) : null}
      {!row.realisation.realised ? (
        <p className="text-xs text-muted-foreground" title="CGST Rule 96A: IGST becomes payable with interest if still unpaid">
          Rule 96A: {formatDate(row.realisation.rule96aDueDate)}
        </p>
      ) : null}
    </div>
  );
}

function Payments({ row }: { row: ExportInvoiceRow }) {
  if (!row.realisation.payments.length) return <span className="text-xs text-muted-foreground">No payment yet</span>;
  return (
    <ul className="space-y-1">
      {row.realisation.payments.map((payment) => (
        <li key={payment.id} className="text-xs">
          <span className="font-medium">{payment.number}</span> · {formatDate(payment.date)} ·{' '}
          <Money value={payment.amount} currency={payment.currency} />
          <br />
          {payment.remittanceReference ? (
            <span>
              FIRA/FIRC <span className="font-mono">{payment.remittanceReference}</span>
            </span>
          ) : (
            <span className="text-warning">No FIRA/FIRC recorded</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Problems({ problems }: { problems: string[] }) {
  if (!problems.length) return null;
  return (
    <ul className="space-y-0.5" data-testid="export-invoice-problems">
      {problems.map((problem) => (
        <li key={problem} className="flex items-start gap-1 text-xs text-warning">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
          {problem}
        </li>
      ))}
    </ul>
  );
}

interface InvoiceListProps {
  rows: ExportInvoiceRow[];
  base: string;
  canManage: boolean;
  onClose: (row: ExportInvoiceRow) => void;
}

function InvoiceTable({ rows, base, canManage, onClose }: InvoiceListProps) {
  return (
    <Table data-testid="export-invoices">
      <THead>
        <TR className="hover:bg-transparent">
          <TH>Invoice</TH>
          <TH>Customer</TH>
          <TH numeric>Amount</TH>
          <TH>Realisation</TH>
          <TH>Payments · FIRA/FIRC</TH>
          <TH>EDPMS</TH>
          {canManage ? <TH aria-label="Actions" /> : null}
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.documentId} className="align-top" data-testid="export-invoice-row">
            <TD wrap className="max-w-[16rem] align-top">
              <Link to={`/invoices/${row.documentId}`} className="font-medium text-primary hover:underline">
                {row.number}
              </Link>
              <p className="text-xs text-muted-foreground">{formatDate(row.date)}</p>
              {row.lut ? <p className="break-all font-mono text-[11px] text-muted-foreground">LUT {row.lut.arn}</p> : null}
              <div className="mt-1">
                <Problems problems={row.problems} />
              </div>
            </TD>
            <TD wrap className="align-top">
              <p>{row.customerName}</p>
              <p className="text-xs text-muted-foreground">{row.country ?? 'No country'}</p>
            </TD>
            <TD numeric className="align-top">
              <Money value={row.total} currency={row.currency} />
              {row.currency !== base ? (
                <p className="text-xs text-muted-foreground">
                  <Money value={row.baseTotal} currency={base} />
                </p>
              ) : null}
            </TD>
            <TD className="align-top">
              <Realisation row={row} />
            </TD>
            <TD className="align-top">
              <Payments row={row} />
            </TD>
            <TD wrap className="max-w-[12rem] align-top text-xs">
              {closureText(row)}
              {row.closure.note ? <p className="text-muted-foreground">{row.closure.note}</p> : null}
            </TD>
            {canManage ? (
              <TD className="align-top">
                <Button size="sm" variant="outline" onClick={() => onClose(row)} aria-label={`Record closure or extension for ${row.number}`}>
                  Closure / extension
                </Button>
              </TD>
            ) : null}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function InvoiceCards({ rows, base, canManage, onClose }: InvoiceListProps) {
  return (
    <Cards data-testid="export-invoices">
      {rows.map((row) => (
        <CardRow key={row.documentId} className="space-y-2" data-testid="export-invoice-row">
          <CardLine
            title={
              <Link to={`/invoices/${row.documentId}`} className="text-primary hover:underline">
                {row.number}
              </Link>
            }
            meta={`${row.customerName} · ${row.country ?? 'No country'} · ${formatDate(row.date)}`}
            value={<Money value={row.total} currency={row.currency} />}
            sub={row.currency !== base ? <Money value={row.baseTotal} currency={base} /> : undefined}
          />
          <Problems problems={row.problems} />
          <Realisation row={row} />
          <Payments row={row} />
          <CardFacts>
            <span>EDPMS: {closureText(row)}</span>
            {row.lut ? <span className="break-all font-mono">LUT {row.lut.arn}</span> : null}
          </CardFacts>
          {canManage ? (
            <Button size="sm" variant="outline" onClick={() => onClose(row)} aria-label={`Record closure or extension for ${row.number}`}>
              Closure / extension
            </Button>
          ) : null}
        </CardRow>
      ))}
    </Cards>
  );
}

/**
 * Record the month's EDF: when it went in, to whom, and the acknowledgement.
 *
 * The date is the fact that matters - FEMA runs on it - so it is the one
 * required field, prefilled with today because that is when most people
 * open this: on their way back from the bank.
 */
function EdfSheet({ month, today, onClose, onSaved }: { month: EdfMonth; today: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const existing = month.declaration;
  const [filedOn, setFiledOn] = useState(existing?.filedOn ?? today);
  const [authority, setAuthority] = useState<EdfAuthority>(existing?.authority ?? 'ad_bank');
  const [reference, setReference] = useState(existing?.reference ?? '');
  const [extendedTo, setExtendedTo] = useState(existing?.extendedTo ?? '');
  const [note, setNote] = useState(existing?.note ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.put(`/exports/edf/${month.month}`, {
        filedOn,
        authority,
        // Left out rather than sent empty: the record is replaced whole, so
        // an omitted field is a cleared one.
        reference: reference.trim() || undefined,
        extendedTo: extendedTo || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const title = `EDF for ${monthLabel(month.month)}`;

  return (
    <Sheet label={title} onClose={onClose}>
      <SheetHeader title={title} onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (filedOn) save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            One form for the month&rsquo;s {month.invoiceCount === 1 ? 'export invoice' : `${month.invoiceCount} export invoices`}, due at
            the bank by {formatDate(month.dueDate)}.
          </p>
          <Input label="Filed on" type="date" required value={filedOn} onChange={(event) => setFiledOn(event.target.value)} error={error?.fieldError('filedOn')} />
          <SelectField id="edf-authority" label="Filed with" value={authority} onChange={(value) => setAuthority(value as EdfAuthority)} error={error?.fieldError('authority')}>
            {EDF_AUTHORITIES.map((value) => (
              <option key={value} value={value}>
                {EDF_AUTHORITY_LABELS[value]}
              </option>
            ))}
          </SelectField>
          <Input
            label="Acknowledgement"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            maxLength={120}
            hint="The bank's acknowledgement or EDPMS reference - what proves the date."
            error={error?.fieldError('reference')}
          />
          <Input
            label="Extended to"
            type="date"
            value={extendedTo}
            onChange={(event) => setExtendedTo(event.target.value)}
            hint="Only when the bank allowed a later date (Reg 3(2)(c))."
            error={error?.fieldError('extendedTo')}
          />
          <Textarea label="Note" rows={3} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} error={error?.fieldError('note')} />
          {existing ? null : (
            <p className="text-xs text-muted-foreground">Once it is recorded you can attach the signed form and the bank&rsquo;s acknowledgement to it.</p>
          )}
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!filedOn} data-testid="edf-save">
            {existing ? 'Save changes' : 'Record filing'}
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * What happened to one invoice's EDPMS entry, and any extension the bank gave.
 *
 * Every field is sent, null when empty: the form shows the whole record, so
 * clearing a field here is meant to clear it there. Closing on our own
 * declaration is offered only where the server says the invoice is within
 * the ₹10 lakh it is allowed for - or where it was already recorded that
 * way, so an edit does not silently change the route.
 */
function ClosureSheet({ row, onClose, onSaved }: { row: ExportInvoiceRow; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [closedOn, setClosedOn] = useState(row.closure.closedOn ?? '');
  const [route, setRoute] = useState<EdpmsClosureRoute | ''>(row.closure.route ?? '');
  const [extendedTo, setExtendedTo] = useState(row.realisation.extendedTo ?? '');
  const [note, setNote] = useState(row.closure.note ?? '');
  const routes = (Object.keys(EDPMS_CLOSURE_ROUTE_LABELS) as EdpmsClosureRoute[]).filter(
    (value) => value !== 'self_declaration' || row.closure.selfDeclarationAllowed || row.closure.route === 'self_declaration',
  );

  const save = useMutation({
    mutationFn: () =>
      api.put(`/exports/invoices/${row.documentId}/closure`, {
        closedOn: closedOn || null,
        route: route || null,
        realisationExtendedTo: extendedTo || null,
        note: note.trim() || null,
      }),
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const routeMissing = !!closedOn && !route;
  const title = `${row.number}: EDPMS and realisation`;

  return (
    <Sheet label={title} onClose={onClose}>
      <SheetHeader title={title} onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (!routeMissing) save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            {row.customerName} · {formatDate(row.date)}. Payment is due by {formatDate(row.realisation.dueDate)}; once it is in, the
            bank closes the EDPMS entry against the FIRA/FIRC.
          </p>
          <Input
            label="Closed on"
            type="date"
            value={closedOn}
            onChange={(event) => setClosedOn(event.target.value)}
            hint="The day the EDPMS entry was closed. Leave empty while it is open."
            error={error?.fieldError('closedOn')}
          />
          <SelectField
            id="closure-route"
            label="How it was closed"
            value={route}
            onChange={(value) => setRoute(value as EdpmsClosureRoute | '')}
            error={error?.fieldError('route') ?? (routeMissing ? 'Say how the entry was closed - by the bank, or on your declaration.' : undefined)}
          >
            <option value="">Not closed yet</option>
            {routes.map((value) => (
              <option key={value} value={value}>
                {EDPMS_CLOSURE_ROUTE_LABELS[value]}
              </option>
            ))}
          </SelectField>
          <Input
            label="Realisation extended to"
            type="date"
            value={extendedTo}
            onChange={(event) => setExtendedTo(event.target.value)}
            hint={`Only when the bank has extended the date payment is due - later than ${formatDate(row.realisation.dueDate)}.`}
            error={error?.fieldError('realisationExtendedTo')}
          />
          <Textarea label="Note" rows={3} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} error={error?.fieldError('note')} />
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={routeMissing} data-testid="closure-save">
            Save
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
